import { ITextElementDataNode, IVideoLibraryAsset } from './index';
import { omit } from '@studio/utils/utils';
import { ICollection } from './array';
import { IAsset, IImageLibraryAsset } from './asset';
import { IColor } from './color';
import { IElementProperty } from './element';
import { IFeed } from './feed';
import { IFontStyle } from './font';
import { IFontFamily, IFontFamilyStyle } from './font-families';
import { IImageAsset } from './image';
import { IElementDataNode, IImageElementDataNode, IVideoElementDataNode, NodeKind } from './nodes';
import { IBorder, IFilterMap, IPadding, IShadow, ITextShadow } from './style';
import { IText, ITextElementCharacterProperties, ITextElementProperties } from './text';
import { IVersion, IVersionedText, IVersionProperty } from './version';
import { IWidgetLibraryAsset } from './widget';
import { IVideoAsset } from '@creative/elements/video/video';

export interface IProperty {
    id: string;
    name: string;
    value?: OneOfElementPropertyValue;
    label?: string;
}

export interface IElementValues {
    versionProperties: IVersionProperty[];
    originalVersionProperties: IVersionProperty[];
    images?: ICollection<IImageAsset> | IImageLibraryAsset[];
    videos?: ICollection<IVideoAsset> | IVideoLibraryAsset[];
    fontFamilies: IFontFamily[];
}

export interface IElementPropertyValues extends IElementValues {
    elementProperties: ICollection<IElementProperty>;
    newOriginalVersionId?: string;
}

export interface IElementPropertyCopyValues {
    versions: ICollection<IVersion>;
    elementProperties: ICollection<IElementProperty>;
    originalVersionProperties: IVersionProperty[];
    newOriginalVersionId?: string;
    images?: ICollection<IImageAsset>;
    videos?: ICollection<IVideoAsset>;
    fontFamilies: IFontFamily[];
}

export type OneOfElementPropertyValue = IFontFamilyStyle
    | IAsset
    | string
    | IVersionedText
    | IText
    | IFeed;
export type WidgetPropertyKeys = keyof typeof widgetPropertyToUnitMap;
// This is the thing
export type OneOfElementPropertyKeys = keyof typeof propertyToUnitMap;
export type OneOfTextElementPropertyKeys = keyof typeof textPropertyToUnitMap;
export type OneOfImageElementPropertyKeys = keyof typeof imagePropertyToUnitMap;
export type OneOfViewNodesSharedPropertyKeys = keyof typeof sharedPropertyToUnitMap;

export interface ICustomProperty extends IElementProperty { }

// This should probably be all type of the properties added together
// export type PropertyValueTyped = Exclude<ITextElementCharacterProperties[keyof ITextElementCharacterProperties], undefined>;

export type PropertyValue =
    string |
    number |
    boolean |
    IColor |
    IShadow[] |
    ITextShadow[] |
    IFilterMap |
    IBorder |
    IFontStyle |
    IImageAsset |
    IPadding |
    IWidgetLibraryAsset |
    ICustomProperty[] |
    IFeed |
    IVersionedText;

export const characterPropertyToUnitMap: Record<keyof ITextElementCharacterProperties, string> = {
    'font': 'FontStyle',
    'textColor': 'Color',
    'fontSize': 'number',
    'lineHeight': 'number',
    'characterSpacing': 'number',
    'uppercase': 'boolean',
    'underline': 'boolean',
    'strikethrough': 'boolean',
    'textShadows': 'TextShadow[]',
    'variable': 'Feed',

    // A helper property to display, font family in dropdown, when font style is undefined.
    '__fontFamilyId': 'string'
};

const textPropertiesOnlyToUnitMap: Record<keyof ITextElementProperties, string> = {
    'maxRows': 'integer',
    'padding': 'Padding',
    'textOverflow': 'enum',
    'horizontalAlignment': 'enum',
    'verticalAlignment': 'enum',
};

export const textPropertyToUnitMap = {
    ...characterPropertyToUnitMap,
    ...textPropertiesOnlyToUnitMap
};

function getObjectKeys<O extends {}>(object: O): (keyof O)[] {
    return Object.keys(object) as (keyof O)[];
}


export const textProperties = getObjectKeys(textPropertyToUnitMap);

export const characterProperties = getObjectKeys(omit(characterPropertyToUnitMap, '__fontFamilyId'));

export const widgetPropertyToUnitMap = {
    'js': 'string',
    'ts': 'string',
    'html': 'string',
    'css': 'string',
    'customProperties': 'CustomProperty[]',
    'widgetReference': 'string',
    'bannerflowLibraryWidgetReference': 'string',
    'select': 'CustomProperty[]',
    'number': 'number',
    'image': 'ImageAsset',
    'boolean': 'boolean',
    'color': 'Color',
    'widgetAsset': 'WidgetAsset',
};

// The keyframe types stuff is a bit weird, is this really how it's supposed to be constructed?
type ImagePropertyMap =
    { imageReference: 'string' } &
    Pick<
        IImageElementDataNode,
        'imageAsset' | 'imageSettings'
    >;
const imagePropertyToUnitMap: Record<keyof ImagePropertyMap, string> | 'imageReference' = {
    'imageReference': 'string',
    'imageAsset': 'ImageAsset',
    'imageSettings': 'ImageSettings',
};

type VideoPropertyMap = Pick<IVideoElementDataNode, 'videoAsset' | 'videoSettings'>;
const videoPropertyToUnitMap: Record<keyof VideoPropertyMap, string> = {
    'videoAsset': 'VideoAsset',
    'videoSettings': 'VideoSettings'
};

// Seems to have some types from states, why?
type SharedPropertyMap = Omit<
    IElementDataNode<NodeKind>,
    | 'name'
    | 'imageAsset'
    | '__rootNode'
    | '__parentNode'
    | 'elementCid'
    | 'cidIndex'
    | 'parentId'
    | 'id'
    | 'kind'>
    & { 'fontReference': 'string' }
    & Pick<ITextElementDataNode, 'textColor' | 'content'>;

export const sharedPropertyToUnitMap: Record<keyof SharedPropertyMap, string> = {
    'x': 'number',
    'y': 'number',
    'width': 'number',
    'height': 'number',
    'duration': 'number',
    'fill': 'Color',
    'textColor': 'Color',
    'originX': 'number',
    'originY': 'number',
    'rotationX': 'number',
    'rotationY': 'number',
    'rotationZ': 'number',
    'radius': 'number',
    'opacity': 'number',
    'shadows': 'Shadow[]',
    'filters': 'FilterMap',
    'border': 'Border',
    'hidden': 'boolean',
    'locked': 'boolean',
    'mirrorX': 'boolean',
    'mirrorY': 'boolean',
    'scaleX': 'number',
    'scaleY': 'number',
    'ratio': 'number',
    'feed': 'Feed',
    'states': 'State[]',
    'animations': 'Animation[]',
    'actions': 'Action[]',
    'time': 'number',
    // TODO: SHOULD NOT BE IN SHARED
    'fontReference': 'string',
    'content': 'text'
};

export const statePropertyToUnitMap = {
    ...omit(sharedPropertyToUnitMap,
        'states',
        'animations',
        'actions',
        'feed',
        'time',
        'duration',
        'hidden',
        'locked',
        'width',
        'height',
        // Currently not supported
        'fontReference',
        'content'
    ),
    'name': 'enum'
};

export const propertyToUnitMap = {
    ...textPropertyToUnitMap,
    ...widgetPropertyToUnitMap,
    ...imagePropertyToUnitMap,
    ...videoPropertyToUnitMap,
    ...sharedPropertyToUnitMap,
    ...statePropertyToUnitMap
};

export const properties = getObjectKeys(propertyToUnitMap);
export const sharedProperties = getObjectKeys(sharedPropertyToUnitMap);

/**
 * Element properties that need to be scaled in order to scale a creative to any particular size
 */
export const ScalableProperties = ['x', 'y', 'width', 'height', 'scaleX', 'scaleY', 'fitPositionX', 'fitPositionY', 'radius', 'border', 'fontSize', 'padding', 'shadows', 'textShadows'];

export function isElementProperty(key: string): key is OneOfElementPropertyKeys {
    return properties.some(p => key === p);
}

export const propertyMaxValues = {
    opacity: 1
};

export const propertyMinValues = {
    radius: 0,
    opacity: 0,
    scaleX: 0,
    scaleY: 0,
};
